import { v4 } from "uuid";

import type { UUID } from "@thalamos/aspire-common";
import { getBaseFormTemplate } from "@thalamos/aspire-common";

import { api } from "../../../api.js";
import { refusalReason } from "../../../helpers/classifyApiFailure.js";

export type LinkedMcaFormStart =
  { ok: true; formDraftId: UUID } | { ok: false; refusal: string | undefined };

/**
 * Adds a form to an existing MCA assessment and opens a draft of it, which is
 * how one MCA form comes to be linked to another: they share the assessment's
 * form context. No work item is created — the caller already holds one on the
 * assessment, which is what grants them access to the new form.
 *
 * Returns the draft id to navigate to or, on failure, the server's reason for
 * refusing when it gave one.
 */
export async function startLinkedMcaForm({
  formContextId,
  patientId,
  formTemplateId,
}: {
  formContextId: UUID;
  patientId: UUID;
  formTemplateId: string;
}): Promise<LinkedMcaFormStart> {
  const template = getBaseFormTemplate(formTemplateId);
  if (!template) return { ok: false, refusal: undefined };

  const formId = v4();
  const formDraftId = v4();

  const createFormResult = await api.forms.create(formId, {
    formContext: { mode: "existing", id: formContextId },
    formTemplate: { id: template.id, version: template.version },
    patientId,
  });

  if (createFormResult.status !== 204) {
    return { ok: false, refusal: refusalReason(createFormResult) };
  }

  const createDraftResult = await api.drafts.create(formDraftId, { formId });

  return createDraftResult.status === 201
    ? { ok: true, formDraftId }
    : { ok: false, refusal: refusalReason(createDraftResult) };
}
